import React, { useEffect, useState } from 'react';
import { Bot, ChevronDown, ChevronUp, Loader2, MessageSquare, AlertCircle } from 'lucide-react';
import { getContactChatSessions } from '../../../api/contact';

function formatRelativeTime(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function SessionCard({ session }) {
    const [expanded, setExpanded] = useState(false);

    const roleLabel = (role) => {
        if (role === 'bot') return 'GeeVee';
        if (role === 'agent') return 'Agent';
        if (role === 'user') return 'Buyer';
        return role;
    };

    return (
        <div className="border border-gray-100 rounded-xl overflow-hidden">
            <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-start justify-between gap-4 p-4 text-left hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-[#55c5d0]/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-[#55c5d0]" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-semibold text-gray-900 text-sm">
                            {session.flow_label || session.flow} · Session #{session.id}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                            {formatRelativeTime(session.created_at)} · {session.message_count} messages · {session.status}
                        </div>
                        {session.summary && (
                            <p className="text-sm text-gray-600 mt-2 line-clamp-2">{session.summary}</p>
                        )}
                    </div>
                </div>
                {expanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />}
            </button>

            {expanded && (
                <div className="border-t border-gray-100 bg-gray-50 p-4 space-y-3 max-h-96 overflow-y-auto">
                    {session.messages?.map((msg) => (
                        <div
                            key={msg.id}
                            className={`text-sm rounded-lg px-3 py-2 ${
                                msg.role === 'user'
                                    ? 'bg-white border border-gray-200 ml-8'
                                    : msg.role === 'agent'
                                    ? 'bg-blue-50 border border-blue-100 mr-8'
                                    : 'bg-[#55c5d0]/10 border border-[#55c5d0]/20 mr-8'
                            }`}
                        >
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                                {roleLabel(msg.role)}
                            </div>
                            <div className="text-gray-800 whitespace-pre-wrap">{msg.content}</div>
                        </div>
                    ))}
                    {session.lead_id && (
                        <p className="text-xs text-emerald-600 font-medium">Linked to Lead #{session.lead_id}</p>
                    )}
                </div>
            )}
        </div>
    );
}

export default function ContactConversations({ contactId }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!contactId) return;
        let cancelled = false;

        (async () => {
            setLoading(true);
            setError('');
            try {
                const res = await getContactChatSessions(contactId);
                if (!cancelled) setSessions(res.data?.sessions || []);
            } catch (e) {
                if (!cancelled) setError('Failed to load conversations.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [contactId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Loading conversations…
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center gap-2 text-red-600 py-8 justify-center">
                <AlertCircle className="w-5 h-5" />
                {error}
            </div>
        );
    }

    if (sessions.length === 0) {
        return (
            <div className="text-center py-12 text-gray-400">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No chatbot or messenger conversations yet.</p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {sessions.map((session) => (
                <SessionCard key={session.id} session={session} />
            ))}
        </div>
    );
}
